import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowUpCircle, CheckCircle, XCircle, Clock, Image, Loader2, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWalletTopupsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/wallet-topups`, { headers: getAuthHeaders() });
      setTopups(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setActionLoading(id);
    try {
      const res = await axios.put(`${API}/admin/wallet-topups/${id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error('Error'); } finally { setActionLoading(null); }
  };

  const reject = async (id) => {
    if (!window.confirm('Rechazar esta recarga?')) return;
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/wallet-topups/${id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success('Recarga rechazada');
      load();
    } catch (e) { toast.error('Error'); } finally { setActionLoading(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-wallet-topups-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Recargas Pendientes</h1>
        <p className="text-slate-500">Aprueba o rechaza las solicitudes de recarga de clientes</p>
      </div>

      {topups.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <ArrowUpCircle className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay recargas pendientes</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {topups.map((t) => (
            <Card key={t.id} className="p-5 border-0 shadow-sm" data-testid={`topup-${t.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                    <span className="font-bold text-xl text-slate-900">${t.amount?.toFixed(2)}</span>
                    <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pendiente
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 space-y-1">
                    <p><strong>Cliente:</strong> {t.client_name} ({t.client_email})</p>
                    <p><strong>Titular transferencia:</strong> {t.holder_name}</p>
                    <p><strong>Descripcion:</strong> {t.description}</p>
                    <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString('es-ES')}</p>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2">
                  {t.proof_image && (
                    <button
                      onClick={() => setPreviewImg(`${process.env.REACT_APP_BACKEND_URL}${t.proof_image}`)}
                      className="w-24 h-24 rounded-lg border-2 border-slate-200 overflow-hidden hover:border-blue-500 transition-colors"
                      data-testid={`proof-preview-${t.id}`}
                    >
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${t.proof_image}`} alt="Comprobante" className="w-full h-full object-cover" />
                    </button>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approve(t.id)} disabled={actionLoading === t.id} className="bg-green-600 hover:bg-green-700" data-testid={`approve-topup-${t.id}`}>
                      {actionLoading === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" /> Aprobar</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(t.id)} disabled={actionLoading === t.id} className="text-red-600 border-red-200" data-testid={`reject-topup-${t.id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Rechazar
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImg && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPreviewImg(null)} data-testid="proof-modal">
          <img src={previewImg} alt="Comprobante" className="max-w-full max-h-[80vh] rounded-lg shadow-xl" />
        </div>
      )}
    </div>
  );
};

export default AdminWalletTopupsPage;
